import { useState } from "react";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshTextInput } from "../base_components/sesh_text_input";
import { SeshWarningButton } from "../base_components/sesh_warning_button";

export function AdminBulkDeleteEvents() {
	const [guildId, setGuildId] = useState("");
	const [authorId, setAuthorId] = useState("");
	// The previewed counts, along with the exact inputs they were fetched for.
	// We only allow deleting when the current inputs still match this preview,
	// so a stale count can never be confirmed against.
	const [preview, setPreview] = useState<{
		guildId: string;
		authorId: string;
		events: number;
		associated: { key: string; label: string; count: number }[];
	} | null>(null);
	const [showConfirm, setShowConfirm] = useState(false);
	const [result, setResult] = useState<string | null>(null);

	const inputsFilled = guildId.trim().length > 0 && authorId.trim().length > 0;
	const previewMatchesInputs =
		preview !== null &&
		preview.guildId === guildId.trim() &&
		preview.authorId === authorId.trim();

	const previewMutation = trpc.admin.previewBulkDeleteEvents.useMutation();

	const deleteMutation = trpc.admin.bulkDeleteEvents.useMutation({
		onSuccess: (data) => {
			setShowConfirm(false);
			setResult(`Deleted ${data.deleted} event(s) and their associated data.`);
			setPreview(null);
		},
	});

	async function runPreview() {
		setResult(null);
		setPreview(null);
		const guild_id = guildId.trim();
		const author_id = authorId.trim();
		try {
			const data = await previewMutation.mutateAsync({ guild_id, author_id });
			setPreview({
				guildId: guild_id,
				authorId: author_id,
				events: data.events,
				associated: data.associated,
			});
		} catch {
			// Error surfaced via previewMutation.isError below.
		}
	}

	function onInputChange(setter: (val: string) => void) {
		return (val: string) => {
			setter(val);
			// Any edit invalidates a previously fetched count.
			setPreview(null);
			setResult(null);
		};
	}

	return (
		<div className="flex max-w-xl flex-col gap-6">
			<div className="text-slate-300">
				Deletes <span className="font-bold">all</span> events created by a
				given author in a given guild, along with their RSVPs, reminders, and
				calendar sync data. Both fields are required.
			</div>

			<div className="flex flex-col gap-2">
				<div className="font-bold">Guild ID</div>
				<SeshTextInput
					input_props={{
						value: guildId,
						onChange: (e) =>
							onInputChange(setGuildId)(e.currentTarget.value),
						placeholder: "e.g. 616750784087982109",
					}}
				/>
			</div>

			<div className="flex flex-col gap-2">
				<div className="font-bold">Author ID</div>
				<SeshTextInput
					input_props={{
						value: authorId,
						onChange: (e) =>
							onInputChange(setAuthorId)(e.currentTarget.value),
						placeholder: "e.g. 1466252954964332666",
					}}
				/>
			</div>

			<div className="flex gap-2">
				<SeshPrimaryButton
					className="!w-auto px-4"
					is_loading={previewMutation.isLoading}
					disabled={!inputsFilled}
					onClick={runPreview}
				>
					Preview count
				</SeshPrimaryButton>
			</div>

			{previewMutation.isError && (
				<div className="text-red-400">
					Error: {previewMutation.error.message}
				</div>
			)}

			{previewMatchesInputs && (
				<div className="flex flex-col gap-3 rounded-lg border border-gray-600 bg-gray-800 p-4">
					<div className="text-slate-200">
						<span className="text-2xl font-bold">{preview.events}</span>{" "}
						event(s) will be deleted.
					</div>
					<div className="flex flex-col gap-1 text-sm text-slate-400">
						<div className="font-semibold text-slate-300">
							Associated rows that will also be deleted:
						</div>
						{preview.associated.map((row) => (
							<div key={row.key} className="flex justify-between">
								<span>{row.label}</span>
								<span className="font-mono">{row.count}</span>
							</div>
						))}
						<div className="mt-1 text-xs text-slate-500">
							RSVPs and server reminders are removed via database
							cascade and are not counted here.
						</div>
					</div>
					{preview.events > 0 ? (
						<div className="flex gap-2">
							<SeshWarningButton
								className="!w-auto px-4"
								onClick={() => setShowConfirm(true)}
							>
								Delete {preview.events} event(s)
							</SeshWarningButton>
						</div>
					) : (
						<div className="text-slate-400">
							Nothing to delete for this guild + author.
						</div>
					)}
				</div>
			)}

			{result && (
				<div className="rounded-lg border border-green-700 bg-green-900/30 p-4 text-green-300">
					{result}
				</div>
			)}

			{deleteMutation.isError && (
				<div className="text-red-400">
					Error: {deleteMutation.error.message}
				</div>
			)}

			<DashboardUtils.SeshModal
				is_active={showConfirm}
				onClose={() => setShowConfirm(false)}
				title="Confirm bulk delete"
				content={
					<div className="flex flex-col gap-4">
						<p className="whitespace-pre-line text-slate-300">
							{`This will permanently delete ${
								preview?.events ?? 0
							} event(s) created by author ${authorId.trim()} in guild ${guildId.trim()}, along with all of their RSVPs, reminders, and calendar sync data.\n\nThis cannot be undone.`}
						</p>
						<div className="flex justify-end gap-2">
							<SeshPrimaryButton
								className="!w-auto bg-slate-600 px-4 hover:bg-slate-700"
								onClick={() => setShowConfirm(false)}
							>
								Cancel
							</SeshPrimaryButton>
							<SeshWarningButton
								className="!w-auto px-4"
								is_loading={deleteMutation.isLoading}
								onClick={() =>
									deleteMutation.mutate({
										guild_id: guildId.trim(),
										author_id: authorId.trim(),
									})
								}
							>
								Delete {preview?.events ?? 0} event(s)
							</SeshWarningButton>
						</div>
					</div>
				}
			/>
		</div>
	);
}
